import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, SimpleGrid, Card, Group, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, StatusBadge, Table } from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../i18n/I18nProvider';
import { HubCard } from '../../components/HubCard';
import { StatCard } from '../../components/StatCard';
import { useIntegrations } from '../../data/useIntegrations';
import { formatInteger, formatRelative } from '../../lib/format';
import { ArchiveIcon, PulseIcon } from '../../components/hubIcons';
import { SystemTabs } from './components/SystemTabs';
import './system-hub.css';

const MUTED = 'var(--rr-color-text-muted)';
const REFRESH_MS = 30_000;

const BellIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z" /><path d="M10 18a2 2 0 0 0 4 0" />
  </svg>
);
const ShieldIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3 5 5.6v5.3c0 4 2.8 7.6 7 9.1 4.2-1.5 7-5.1 7-9.1V5.6z" /><path d="M9.2 12.1 11.3 14.2 15 10.3" />
  </svg>
);
const QueueIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="4.5" rx="1.5" /><rect x="3" y="12" width="12" height="4.5" rx="1.5" />
    <path d="M18 14.25h3" />
  </svg>
);

const smallIcon = (d: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{d}</svg>
);
const HeartIcon = smallIcon(<path d="M12 20s-7.5-4.6-7.5-9.5A4 4 0 0 1 12 8a4 4 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z" />);
const DownIcon = smallIcon(<><circle cx="12" cy="12" r="8.5" /><path d="M8.5 8.5l7 7M15.5 8.5l-7 7" /></>);
const GridIcon = smallIcon(<><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></>);
const ClockIcon = smallIcon(<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 1.8" /></>);
const InboxIcon = smallIcon(<><path d="M3.5 13.5h5l1.5 3h4l1.5-3h5" /><path d="M6 4.5h12l2.5 9v6h-17v-6z" /></>);
const UsersIcon = smallIcon(<><circle cx="9" cy="9" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6.2a3.2 3.2 0 0 1 0 5.6M17 19a5.5 5.5 0 0 0-1.6-3.9" /></>);

/**
 * The channels the alert dispatcher looks up, in the order it looks them up.
 * Shared shape with the Alerts page, kept short here because the hub only
 * reports whether each one is wired.
 */
const CHANNELS: { provider: string; labelKey: string }[] = [
  { provider: 'pagerduty', labelKey: 'system.alerts.channel.pagerduty' },
  { provider: 'slack_alerts', labelKey: 'system.alerts.channel.slack' },
  { provider: 'linear_issues', labelKey: 'system.alerts.channel.linear' }
];

function toneFor(status: string): 'healthy' | 'warning' | 'down' {
  if (status === 'healthy') return 'healthy';
  if (status === 'down') return 'down';
  return 'warning';
}

export default function SystemPage() {
  const t = useT();

  // The same query keys the four destinations use, so opening the hub warms
  // their caches and the hub answers "is everything fine" itself rather than
  // asking the operator to click through to find out.
  const healthQ = useQuery({
    queryKey: ['admin', 'ops', 'health'],
    queryFn: () => admin.listServiceHealth(),
    refetchInterval: REFRESH_MS
  });
  const queuesQ = useQuery({
    queryKey: ['admin', 'ops', 'queues'],
    queryFn: () => admin.listQueues(),
    refetchInterval: REFRESH_MS
  });
  const backupsQ = useQuery({
    queryKey: ['admin', 'ops', 'backups'],
    queryFn: () => admin.listBackups()
  });
  const integrationsQ = useIntegrations();

  const services = healthQ.data?.data ?? [];
  const healthy = services.filter((r) => r.status === 'healthy').length;
  const unhealthy = services.filter((r) => r.status === 'unhealthy').length;
  const down = services.filter((r) => r.status === 'down').length;
  const slowest = services.reduce<typeof services[number] | null>(
    (worst, r) => (worst === null || r.duration_ms > worst.duration_ms ? r : worst),
    null
  );
  // A probe that failed is not a platform with zero services, so every figure
  // derived from it says "n/a" instead of a confident zero.
  const healthUnknown = Boolean(healthQ.error);

  const queues = queuesQ.data?.data ?? [];
  const brokerError = queuesQ.data?.meta?.error;
  const queuesUnknown = Boolean(queuesQ.error) || Boolean(brokerError);
  const ready = queues.reduce((sum, q) => sum + (q.messages_ready ?? 0), 0);
  const unacked = queues.reduce((sum, q) => sum + (q.messages_unacked ?? 0), 0);
  const consumers = queues.reduce((sum, q) => sum + (q.consumers ?? 0), 0);
  const starved = queues.filter((q) => (q.consumers ?? 0) === 0 && (q.messages ?? 0) > 0).length;
  const topQueues = [...queues].sort((a, b) => (b.messages ?? 0) - (a.messages ?? 0)).slice(0, 8);

  const channels = CHANNELS.map((c) => ({
    ...c,
    enabled: (integrationsQ.data?.data ?? []).some((r) => r.provider_code === c.provider && r.enabled)
  }));
  const liveChannels = channels.filter((c) => c.enabled).length;

  const na = t('common.notAvailable');
  const healthStat = (n: number) => (healthUnknown ? na : formatInteger(n));
  const queueStat = (n: number) => (queuesUnknown ? na : formatInteger(n));

  const healthBadge = healthUnknown
    ? <StatusBadge status="inactive" size="xs">{na}</StatusBadge>
    : services.length === 0
      ? null
      : down > 0
        ? <StatusBadge status="down" size="xs">{t('system.hub.servicesDown', { count: down })}</StatusBadge>
        : unhealthy > 0
          ? <StatusBadge status="warning" size="xs">{t('system.hub.servicesUnhealthy', { count: unhealthy })}</StatusBadge>
          : <StatusBadge status="healthy" size="xs">{t('system.hub.allHealthy', { count: services.length })}</StatusBadge>;

  const alertsBadge = integrationsQ.error
    ? <StatusBadge status="inactive" size="xs">{na}</StatusBadge>
    : liveChannels === 0
      ? <StatusBadge status="warning" size="xs">{t('system.hub.noChannels')}</StatusBadge>
      : <StatusBadge status="healthy" size="xs">{t('system.hub.channelsLive', { count: liveChannels })}</StatusBadge>;

  const queuesBadge = queuesUnknown
    ? <StatusBadge status="inactive" size="xs">{na}</StatusBadge>
    : starved > 0
      ? <StatusBadge status="warning" size="xs">{t('system.hub.queuesStarved', { count: starved })}</StatusBadge>
      : <StatusBadge status="healthy" size="xs">{t('system.hub.queuesOk', { count: queues.length })}</StatusBadge>;

  const probedAt = healthQ.dataUpdatedAt > 0
    ? t('system.hub.probedAt', { when: formatRelative(new Date(healthQ.dataUpdatedAt).toISOString()) })
    : t('system.health.neverChecked');

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('system.title')}
        description={t('system.description')}
      />

      <SystemTabs />

      {healthQ.error && <Alert color="warning">{t('system.hub.healthUnavailable')}</Alert>}
      {brokerError && <Alert color="warning">{t('queues.unreachable', { error: brokerError })}</Alert>}

      <SimpleGrid cols={{ base: 2, md: 3, xl: 6 }} spacing="md">
        <StatCard
          icon={HeartIcon}
          tone="success"
          label={t('system.hub.stats.healthy')}
          value={healthStat(healthy)}
          hint={healthUnknown ? undefined : t('system.hub.stats.ofServices', { total: services.length })}
          loading={healthQ.isLoading}
          withRule
        />
        <StatCard
          icon={DownIcon}
          tone="error"
          label={t('system.hub.stats.down')}
          value={healthStat(down + unhealthy)}
          hint={healthUnknown ? undefined : t('system.hub.stats.downHint', { degraded: unhealthy })}
          loading={healthQ.isLoading}
          withRule
        />
        <StatCard
          icon={GridIcon}
          tone="neutral"
          label={t('system.hub.stats.services')}
          value={healthStat(services.length)}
          hint={t('system.hub.stats.servicesHint')}
          loading={healthQ.isLoading}
          withRule
        />
        {/* The endpoint reports one probe per service, so the honest headline
            is the slowest of those probes, not a percentile nobody measured. */}
        <StatCard
          icon={ClockIcon}
          tone="info"
          label={t('system.hub.stats.slowestProbe')}
          value={healthUnknown || !slowest ? na : t('system.hub.stats.ms', { ms: slowest.duration_ms })}
          hint={healthUnknown || !slowest ? undefined : slowest.service}
          loading={healthQ.isLoading}
          withRule
        />
        <StatCard
          icon={InboxIcon}
          tone={ready > 0 ? 'warning' : 'neutral'}
          label={t('queues.metrics.readyBacklog')}
          value={queueStat(ready)}
          hint={queuesUnknown ? undefined : t('system.hub.stats.acrossQueues', { count: queues.length })}
          loading={queuesQ.isLoading}
          withRule
        />
        <StatCard
          icon={UsersIcon}
          tone="primary"
          label={t('queues.columns.consumers')}
          value={queueStat(consumers)}
          hint={queuesUnknown ? undefined : t('system.hub.stats.unackedHint', { count: formatInteger(unacked) })}
          loading={queuesQ.isLoading}
          withRule
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2, xl: 5 }} spacing="md">
        <HubCard
          icon={PulseIcon}
          title={t('system.cards.health.title')}
          description={t('system.cards.health.hint')}
          status={healthBadge}
          rows={[{
            label: healthUnknown
              ? t('system.hub.health.unknownRow')
              : down + unhealthy > 0
                ? t('system.hub.health.degradedRow', { count: down + unhealthy })
                : t('system.hub.health.okRow'),
            to: '/system/health'
          }]}
        />
        <HubCard
          icon={BellIcon}
          title={t('system.cards.alerts.title')}
          description={t('system.cards.alerts.hint')}
          status={alertsBadge}
          rows={[{
            label: integrationsQ.error
              ? t('system.hub.alerts.unknownRow')
              : liveChannels === 0
                ? t('system.hub.alerts.noneRow')
                : t('system.hub.alerts.liveRow', { count: liveChannels }),
            to: '/system/alerts'
          }]}
        />
        <HubCard
          icon={ArchiveIcon}
          title={t('system.cards.backups.title')}
          description={t('system.cards.backups.hint')}
          status={<StatusBadge status="pending" size="xs">{t('system.hub.backups.manual')}</StatusBadge>}
          rows={[{ label: t('system.hub.backups.row'), to: '/system/backups' }]}
        />
        <HubCard
          icon={ShieldIcon}
          title={t('system.cards.security.title')}
          description={t('system.cards.security.hint')}
          rows={[{ label: t('system.hub.security.row'), to: '/system/security' }]}
        />
        <HubCard
          icon={QueueIcon}
          title={t('system.cards.queues.title')}
          description={t('system.hub.queues.description')}
          status={queuesBadge}
          rows={[{
            label: queuesUnknown
              ? t('system.hub.queues.unknownRow')
              : t('system.hub.queues.row', { ready: formatInteger(ready), count: queues.length }),
            to: '/queue-monitoring'
          }]}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2, xl: 3 }} spacing="md">
        {/* Health snapshot: the same rows the Health page renders as cards,
            listed here because the hub is where an incident starts. */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Title order={5}>{t('system.hub.snapshot.title')}</Title>
              {healthBadge}
            </Group>
            <Text role="caption" c={MUTED}>{probedAt}</Text>
            {healthUnknown ? (
              <Text role="bodySmall" c={MUTED}>{t('system.hub.snapshot.unavailable')}</Text>
            ) : (
              <div className="rr-hub-scroll">
                <Table density="compact">
                  <Table.Thead>
                    <tr>
                      <th>{t('logs.columns.service')}</th>
                      <th>{t('common.status')}</th>
                      <th style={{ textAlign: 'right' }}>{t('system.hub.snapshot.probeMs')}</th>
                    </tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {services.map((r) => (
                      <tr key={r.service}>
                        <td>{r.service}</td>
                        <td><StatusBadge status={toneFor(r.status)} size="xs">{t(`status.${r.status === 'unhealthy' ? 'degraded' : r.status}`)}</StatusBadge></td>
                        <td style={{ textAlign: 'right' }}>{r.http_status ? formatInteger(r.duration_ms) : na}</td>
                      </tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}
            <Link to="/system/health" className="rr-hub-more">{t('system.hub.snapshot.viewAll')} <span aria-hidden>→</span></Link>
          </Stack>
        </Card>

        {/* Alert channels: which integration rows are live, which is the only
            part of alerting the console can read today. */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Title order={5}>{t('system.hub.channels.title')}</Title>
              {alertsBadge}
            </Group>
            <Stack gap="sm">
              {channels.map((c) => (
                <Group key={c.provider} justify="space-between" align="center" wrap="nowrap">
                  <Text role="bodySmall" fw={600}>{t(c.labelKey)}</Text>
                  <StatusBadge status={c.enabled ? 'healthy' : 'inactive'} size="xs">
                    {c.enabled ? t('status.connected') : t('status.notConfigured')}
                  </StatusBadge>
                </Group>
              ))}
            </Stack>
            <Text role="caption" c={MUTED}>{t('system.hub.channels.hint')}</Text>
            <Link to="/settings/integrations" className="rr-hub-more">{t('system.hub.channels.manage')} <span aria-hidden>→</span></Link>
          </Stack>
        </Card>

        {/* Backup status: the server's own notice, which is all there is. */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Title order={5}>{t('system.hub.backupStatus.title')}</Title>
              <StatusBadge status="pending" size="xs">{t('system.hub.backups.manual')}</StatusBadge>
            </Group>
            <Text role="bodySmall" c={MUTED}>
              {backupsQ.error
                ? t('system.backups.loadError')
                : backupsQ.data?.meta?.notice ?? t('system.backups.noMetadata')}
            </Text>
            <Link to="/system/backups" className="rr-hub-more">{t('system.hub.backupStatus.manage')} <span aria-hidden>→</span></Link>
          </Stack>
        </Card>

        {/* Queue overview: the deepest queues, which is what an operator scans
            for when something is slow. */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Title order={5}>{t('system.hub.queueOverview.title')}</Title>
              {queuesBadge}
            </Group>
            {queuesUnknown ? (
              <Text role="bodySmall" c={MUTED}>{t('system.hub.queueOverview.unavailable')}</Text>
            ) : (
              <div className="rr-hub-scroll">
                <Table density="compact">
                  <Table.Thead>
                    <tr>
                      <th>{t('queues.columns.queue')}</th>
                      <th style={{ textAlign: 'right' }}>{t('queues.columns.ready')}</th>
                      <th style={{ textAlign: 'right' }}>{t('queues.columns.unacked')}</th>
                      <th style={{ textAlign: 'right' }}>{t('queues.columns.consumers')}</th>
                    </tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {topQueues.map((q) => (
                      <tr key={`${q.vhost}/${q.name}`}>
                        <td>{q.name}</td>
                        <td style={{ textAlign: 'right' }}>{formatInteger(q.messages_ready ?? 0)}</td>
                        <td style={{ textAlign: 'right' }}>{formatInteger(q.messages_unacked ?? 0)}</td>
                        <td style={{ textAlign: 'right' }}>{formatInteger(q.consumers ?? 0)}</td>
                      </tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}
            <Link to="/queue-monitoring" className="rr-hub-more">{t('system.hub.queueOverview.viewAll')} <span aria-hidden>→</span></Link>
          </Stack>
        </Card>
      </SimpleGrid>

      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text role="caption" c={MUTED}>
          {healthQ.dataUpdatedAt > 0
            ? t('system.hub.footer.updated', { when: formatRelative(new Date(healthQ.dataUpdatedAt).toISOString()) })
            : t('system.health.neverChecked')}
        </Text>
        <Text role="caption" c={MUTED}>{t('system.hub.footer.refresh', { seconds: REFRESH_MS / 1000 })}</Text>
      </Group>
    </Stack>
  );
}
